import { Form } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import React, { useState } from 'react';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import SuccessIcon from 'assets/icons/SuccessIcon';
import useUserDetail from 'hooks/useUserDetail';
import useThemes from 'hooks/useThemes';
import TextField from 'components/FormField/TextField';
import { MdEmail } from 'react-icons/md';
import FieldError from 'components/FormField/FieldError';
import PasswordField from 'components/FormField/PasswordField';
import { FaLock } from 'react-icons/fa';
import vinpay from 'assets/images/vinpay_logo.png';
import VerticalForm from 'components/Form/VerticalForm';
import { VinpayConnectRequestModel } from 'models/vinpay';
import vinpayApi from 'api/vinpayApi';
import userApi from 'api/userApi';

interface VinpayConnectModalProps {
  visible: boolean;
  closeModal: () => void;
  className?: string;
  hiddenAutoCreate?: boolean;
  onSuccess?: () => void;
}

const VinpayConnectModal: React.FunctionComponent<VinpayConnectModalProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
  const { visible, closeModal, className = '', hiddenAutoCreate = false, onSuccess = null } = props;
  const handleResponseError = useHandleResponseError();
  const t = useAppTranslation();
  const { getUser } = useUserDetail();
  const { currentTheme } = useThemes();
  const [form] = Form.useForm();
  const [haveError, setHaveError] = useState<boolean>(false);

  const onSubmitLoginForm = async (value: VinpayConnectRequestModel) => {
    setIsLoading(true);

    const { ok, error } = await vinpayApi.connect({
      ...value,
    });

    setIsLoading(false);

    if (ok) {
      getUser();
      if (onSuccess) {
        onSuccess();
      } else {
        closeModal();
      }
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const onCreateVinpayAccount = async () => {
    setIsLoading(true);

    const { ok, error } = await userApi.createVinpayAccount();

    setIsLoading(false);

    if (ok) {
      setIsShowSuccess(true);
      getUser();
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const handleFieldsChange = () => {
    setHaveError(form.getFieldsError().some((error) => error.errors.length));
  };

  const onCloseModal = () => {
    resetModal();
    closeModal();
  };

  const resetModal = () => {
    setIsShowSuccess(false);
  };

  return (
    <Modal
      className={className}
      visible={visible}
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      {!isShowSuccess ? (
        <VerticalForm
          className="auth__login relative"
          name="login"
          form={form}
          onFinish={onSubmitLoginForm}
          onFieldsChange={handleFieldsChange}
        >
          <div className="center-content-col gap-3">
            <img src={vinpay} alt="vinpay logo" width={300} />
            <TextField
              name="email"
              placeholder={t('login.placeholder.email')}
              prefix={<MdEmail />}
              rules={[
                {
                  required: true,
                  message: <FieldError message={t('errors.email.required')} />,
                },
                {
                  type: 'email',
                  message: <FieldError message={t('errors.email.invalid')} />,
                },
              ]}
            />
            <PasswordField
              name="password"
              placeholder={t('login.placeholder.password')}
              prefix={<FaLock />}
              rules={[
                {
                  required: true,
                  message: <FieldError message={t('errors.password.required')} />,
                },
              ]}
            />
            <button
              disabled={isLoading || haveError}
              type="submit"
              className={`btn-border-submit connect-button ${
                isLoading || haveError ? 'disabled' : ''
              }`}
            >
              {t('common.connect')}
            </button>
            {!hiddenAutoCreate && (
              <>
                <p className="text-center-line ">OR</p>
                <button
                  onClick={onCreateVinpayAccount}
                  disabled={isLoading || haveError}
                  type="submit"
                  className={`btn-submit connect-button signup-with ${isLoading ? 'disabled' : ''}`}
                >
                  {t('common.createVinpay')}
                </button>
              </>
            )}
          </div>
        </VerticalForm>
      ) : (
        <div className="center-content-col gap-3 withdraw--success">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('common.connectSuccessfully')}</p>
          <button type="button" className="continue" onClick={onCloseModal}>
            {t('withdraw.close')}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default VinpayConnectModal;
